import { z } from 'zod';
import { unfenced } from '../lib/unfenced.js';

/**
 * What the RANGER answers, and what a rangement stores (scenario-succes.md
 * §3.2, issue #177).
 *
 * Two shapes of one thing, and they part for a reason. `rangerOutputSchema` is
 * what a MODEL returns: lenient on the two sentences (a model writes `null`
 * where nothing is missing), because a null that fails the whole answer would
 * cost the retry of §3.4 for a decoration. `rangementObjectiveSchema` and its
 * siblings are what a rangement KEEPS, after `verifiedRangement` has checked
 * every extract against the person's own words: plain strings, read by default
 * (BUILD-787) so a stored rangement never stops reading.
 */

/**
 * The state of an objective in a rangement (§3.2): `portée` when the person's
 * words carry it, `imprécise` when they brush it without carrying it, `vide`
 * when nothing does. French in the stored value, and said so: the screens and
 * the MCP print it as it is, and the model is asked for exactly these words.
 */
export const RANGEMENT_OBJECTIVE_STATES = ['portée', 'imprécise', 'vide'] as const;
export const rangementObjectiveStateSchema = z.enum(RANGEMENT_OBJECTIVE_STATES);
export type RangementObjectiveState = z.infer<typeof rangementObjectiveStateSchema>;

/**
 * Why a passage was read and put nowhere (§3.2, H5): a CLOSED list, so the
 * measure of §10 can count them and the screen can word each one.
 *
 * ASCII codes, because the model writes them in JSON and the stored value is a
 * handle (the French of each is `RANGEMENT_DISCARD_REASON_LABELS`, and the
 * contract of the prompt explains each in a sentence).
 */
export const RANGEMENT_DISCARD_REASONS = [
  'designe-exemple',
  'negatif',
  'generalite',
  'mot-souffle',
  'autre-episode',
  'decrit-objet',
  'autre',
] as const;
export const rangementDiscardReasonSchema = z.enum(RANGEMENT_DISCARD_REASONS);
export type RangementDiscardReason = z.infer<typeof rangementDiscardReasonSchema>;

/** What each reason reads as on a screen. */
export const RANGEMENT_DISCARD_REASON_LABELS: Readonly<Record<RangementDiscardReason, string>> = {
  'designe-exemple': 'désigne l’exemple',
  negatif: 'négatif',
  generalite: 'généralité',
  'mot-souffle': 'validation d’un mot soufflé',
  'autre-episode': 'autre épisode',
  'decrit-objet': 'décrit l’objet, pas l’exécution',
  autre: 'autre',
};

/** The bound of a model's sentence kept on a rangement - met by a defect, never on purpose. */
export const RANGEMENT_TEXT_MAX = 4_000;

/**
 * One extract of a rangement: a passage of the person's reply, and the number
 * of the turn that carries it - the number Sessions prints, so 1-BASED where
 * `SessionTurn.index` is 0-based.
 */
export const rangementExtractSchema = z.object({
  turn: z.number().int().positive(),
  text: z.string().min(1).max(RANGEMENT_TEXT_MAX),
});
export type RangementExtract = z.infer<typeof rangementExtractSchema>;

/** One objective of a rangement, as it is kept (§3.2). */
export const rangementObjectiveSchema = z.object({
  objectiveId: z.string().min(1),
  state: rangementObjectiveStateSchema,
  /** For an `imprécise` or `vide` objective, what is missing, in one sentence. */
  missing: z.string().max(RANGEMENT_TEXT_MAX).default(''),
  /**
   * The ONE reformulation of the rangement, marked as such on the screen: what
   * the objective holds for this example, at the concrete level of the example.
   */
  formula: z.string().max(RANGEMENT_TEXT_MAX).default(''),
  extracts: z.array(rangementExtractSchema).default([]),
});
export type RangementObjective = z.infer<typeof rangementObjectiveSchema>;

/** A passage read and put nowhere, with its turn and the reason (§3.2). */
export const rangementDiscardSchema = z.object({
  turn: z.number().int().positive(),
  text: z.string().min(1).max(RANGEMENT_TEXT_MAX),
  reason: rangementDiscardReasonSchema,
});
export type RangementDiscard = z.infer<typeof rangementDiscardSchema>;

/* ------------------------------------------------------ what a model returns */

/** A sentence a model may leave as `null` or omit: read as the empty one. */
const modelSentence = z
  .string()
  .nullish()
  .transform((sentence) => (sentence ?? '').trim());

const rangerExtractSchema = z.object({
  turn: z.number().int().positive(),
  text: z.string().min(1),
});

/**
 * The JSON the ranger returns (§3.2) - the contract of `rangerSystemParts`
 * written as a schema. The code reads it with `readRangerAnswer`, then checks
 * every extract with `verifiedRangement`: nothing of what the model says about
 * a passage is believed until it is found in the person's own reply.
 */
export const rangerOutputSchema = z.object({
  objectives: z.array(
    z.object({
      objectiveId: z.string().min(1),
      state: rangementObjectiveStateSchema,
      missing: modelSentence,
      formula: modelSentence,
      extracts: z.array(rangerExtractSchema).nullish().default([]),
    }),
  ),
  discarded: z
    .array(
      z.object({
        turn: z.number().int().positive(),
        text: z.string().min(1),
        reason: rangementDiscardReasonSchema,
      }),
    )
    .nullish()
    .default([]),
});
export type RangerOutput = z.infer<typeof rangerOutputSchema>;

/**
 * The answer of the ranger, or `null` when it does not read: a ``` fence is
 * forgiven (`unfenced`), a text that is not JSON or not the contract is not.
 *
 * `null` is what makes the retry of §3.4 fire, and what the call records as
 * `malformed`: one reader for the server and for the screen that says WHY an
 * attempt failed.
 */
export function readRangerAnswer(raw: string): RangerOutput | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(unfenced(raw));
  } catch {
    return null;
  }
  const result = rangerOutputSchema.safeParse(parsed);
  return result.success ? result.data : null;
}
